import camelize from 'camelize';
import get from 'lodash/get';
import Bowser from 'bowser';
import { OT_ERROR_CODES, SCREEN_SHARING_VIDEO_TYPE } from './constants';

const AUDIO_LEVEL_ACTIVITY = 0.12;
const START_TIME_ACTIVITY = 100;
const END_TIME_ACTIVITY = 800;

export function SpeakerDetection(subscriber, startTalking, stopTalking) {
  let activity = null;

  function handleAudioLevelUpdate(event) {
    const now = Date.now();
    if (event.audioLevel > AUDIO_LEVEL_ACTIVITY) {
      if (!activity) {
        activity = { timestamp: now, talking: false };
      } else if (activity.talking) {
        activity.timestamp = now;
      } else if (now - activity.timestamp > START_TIME_ACTIVITY) {
        activity.talking = true;
        if (typeof startTalking === 'function') {
          startTalking(event.target.stream);
        }
      }
    } else if (activity && now - activity.timestamp > END_TIME_ACTIVITY) {
      if (activity.talking) {
        if (typeof stopTalking === 'function') {
          stopTalking(event.target.stream);
        }
      }
      activity = null;
    }
  }

  subscriber.on('audioLevelUpdated', handleAudioLevelUpdate);

  return () => {
    subscriber.off('audioLevelUpdated', handleAudioLevelUpdate);
  };
}

const parseJSONSafely = data => {
  let value;

  try {
    value = JSON.parse(data);
  } catch (e) {
    value = null;
  }

  return value;
};

export function parseUserPayload(data) {
  const parsed = parseJSONSafely(data);
  const result = !parsed ? {} : camelize(parsed);

  const isProvider = get(result, 'isProvider', false);
  const username = get(result, 'username');
  const name = get(result, 'displayableName', username);
  const id = get(result, 'id');
  const avatar = get(result, 'avatar');

  return { name, isProvider, id, avatar };
}

export function getAllMediaDevices() {
  return new Promise(resolve => {
    OT.getDevices((err, devices) => {
      if (err || !devices) return resolve([]);
      return resolve(devices);
    });
  });
}

export function getMediaDevices() {
  return new Promise((resolve, reject) => {
    OT.getDevices((err, devices) => {
      if (err || !devices) {
        return reject(err);
      }

      const audioSourceList = devices.filter(
        ({ kind }) => kind === 'audioInput',
      );
      const videoSourceList = devices.filter(
        ({ kind }) => kind === 'videoInput',
      );

      return resolve({ audioSourceList, videoSourceList });
    });
  });
}

export function formatName(user, isMe, formatMessage, videoType) {
  const you = formatMessage({ id: 'general.labels.you' });
  return `${get(user, 'name')} ${isMe ? you : ''}  ${
    videoType === SCREEN_SHARING_VIDEO_TYPE ? '(sharing screen)' : ''
  }`;
}

export function getOTErrorTranslationKey(name) {
  const browser = Bowser.parse(window.navigator.userAgent);
  switch (name) {
    case OT_ERROR_CODES.MEDIA_ACCESS_DENIED:
      return {
        title: 'virtualVisit.test.canAccessMicroCamera',
        description: 'virtualVisit.test.giveAccessMicroCamera',
      };

    case OT_ERROR_CODES.HARDWARE_UNAVAILABLE:
      return {
        title: 'virtualVisit.error.cameraMicrophoneUnavailable.title',
        description:
          'virtualVisit.error.cameraMicrophoneUnavailable.description',
      };

    case OT_ERROR_CODES.NO_DEVICES_FOUND:
      return {
        title: 'virtualVisit.error.noDevicesFound.title',
        description: 'virtualVisit.error.noDevicesFound',
      };

    case OT_ERROR_CODES.NO_VALID_CONSTRAINTS:
      return {
        title: 'virtualVisit.error.noValidConstraints.title',
        description: 'virtualVisit.error.noValidConstraints',
      };

    case OT_ERROR_CODES.NOT_SUPPORTED:
      return {
        title: 'virtualVisit.error.notSupported.title',
        description: 'virtualVisit.error.notSupported',
      };

    case OT_ERROR_CODES.SESSION_AUTH_ERROR:
      return {
        title: 'virtualVisit.error.expired.title',
        description: 'virtualVisit.error.expired',
      };

    case OT_ERROR_CODES.SESSION_CONNECT_FAILED:
    case OT_ERROR_CODES.SESSION_CONNECT_LIMIT:
    case OT_ERROR_CODES.SESSION_INVALID_ID:
    case OT_ERROR_CODES.NOT_CONNECTED:
      return {
        title: 'virtualVisit.error.sessionExpired.title',
        description: 'virtualVisit.error.sessionExpired',
      };

    default:
      return {
        title: 'error.somethingWrong',
        description: 'errors.appointment.unavailableVirtualRoom',
      };
  }
}

export function normalizeDevices(list) {
  return list.map(({ deviceId, label }) => ({ label, value: deviceId }));
}



// WEBPACK FOOTER //
// ./src/redux/opentok/helpers/utils.js